import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  InputNumber,
  message,
  Popconfirm,
  Space,
  Tag,
  Tooltip,
  Row,
  Col,
  Statistic,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
  EyeOutlined,
  ReloadOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined
} from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;

const AssetTypesManagement = () => {
  const [loading, setLoading] = useState(false);
  const [assetTypes, setAssetTypes] = useState([]);
  const [fieldConfigs, setFieldConfigs] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [fieldModalVisible, setFieldModalVisible] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [fieldOptions, setFieldOptions] = useState([]);
  const [currentFieldType, setCurrentFieldType] = useState('');
  const [form] = Form.useForm();
  const [fieldForm] = Form.useForm();

  useEffect(() => {
    fetchAssetTypes();
  }, []);

  // 获取资产类型列表
  const fetchAssetTypes = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/assets/types', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setAssetTypes(Array.isArray(data.data) ? data.data : []);
      } else {
        message.error(data.message || '获取资产类型失败');
        setAssetTypes([]);
      }
    } catch (error) {
      console.error('获取资产类型失败:', error);
      message.error('获取资产类型失败');
      setAssetTypes([]);
    } finally {
      setLoading(false);
    }
  };

  // 获取字段配置列表
  const fetchFieldConfigs = async (typeId) => {
    try {
      const response = await fetch(`/api/assets/types/${typeId}/fields`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setFieldConfigs(Array.isArray(data.data) ? data.data : []);
      } else {
        setFieldConfigs([]);
      }
    } catch (error) {
      console.error('获取字段配置失败:', error);
      setFieldConfigs([]);
    }
  };

  // 创建/更新资产类型
  const handleSubmit = async (values) => {
    try {
      const url = editingType 
        ? `/api/assets/types/${editingType.id}` 
        : '/api/assets/types';
      
      const method = editingType ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(values)
      });
      
      const data = await response.json();
      
      if (data.success) {
        message.success(editingType ? '更新资产类型成功' : '创建资产类型成功');
        setModalVisible(false);
        form.resetFields();
        setEditingType(null);
        fetchAssetTypes();
      } else {
        message.error(data.message || '操作失败');
      }
    } catch (error) {
      console.error('操作失败:', error);
      message.error('操作失败');
    }
  };

  // 删除资产类型
  const handleDelete = async (id) => {
    try {
      const response = await fetch(`/api/assets/types/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        message.success('删除资产类型成功');
        fetchAssetTypes();
      } else {
        message.error(data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  // 创建/更新字段配置
  const handleFieldSubmit = async (values) => {
    try {
      const isEditing = editingField && editingField.id;
      const url = isEditing 
        ? `/api/assets/fields/${editingField.id}` 
        : '/api/assets/fields';
      
      const method = isEditing ? 'PUT' : 'POST';
      
      // 处理字段选项
      const fieldOptionsJson = fieldOptionsToJson();
      
      const submitData = {
        ...values,
        asset_type_id: editingType?.id,
        field_options: fieldOptionsJson,
        // 对于资产关联字段，需要包含关联的资产类型ID
        referenced_asset_type_id: values.referenced_asset_type_id
      };
      
      console.log('提交字段配置 - 表单值:', values);
      console.log('提交字段配置 - 提交数据:', submitData);
      console.log('提交字段配置 - 字段类型:', values.field_type);
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(submitData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        message.success(isEditing ? '更新字段配置成功' : '创建字段配置成功');
        setEditingField(null);
        setFieldOptions([]);
        fetchFieldConfigs(editingType?.id);
      } else {
        message.error(data.message || '操作失败');
      }
    } catch (error) {
      console.error('操作失败:', error);
      message.error('操作失败');
    }
  };

  // 删除字段配置
  const handleFieldDelete = async (id) => {
    try {
      const response = await fetch(`/api/assets/fields/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        message.success('删除字段配置成功');
        fetchFieldConfigs(editingType?.id);
      } else {
        message.error(data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  // 打开编辑模态框
  const handleEdit = (record) => {
    setEditingType(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  // 打开字段配置模态框
  const handleFieldEdit = (record) => {
    setEditingField(record);
    setCurrentFieldType(record.field_type || '');
    
    // 初始化字段选项
    let options = [];
    let referencedAssetTypeId = null;
    
    if (record.field_options) {
      try {
        let parsedOptions = record.field_options;
        
        // 如果是字符串，先解析
        if (typeof record.field_options === 'string') {
          parsedOptions = JSON.parse(record.field_options);
        }
        
        if (record.field_type === 'asset_reference') {
          // 资产关联类型
          referencedAssetTypeId = parsedOptions.referenced_asset_type_id;
        } else if (Array.isArray(parsedOptions)) {
          // 如果是数组格式，转换为对象数组
          options = parsedOptions.map((option, index) => ({
            key: Date.now() + index,
            label: option,
            value: option
          }));
        } else if (typeof parsedOptions === 'object' && parsedOptions.options) {
          // 如果是对象格式，直接使用
          options = parsedOptions.options.map((option, index) => ({
            key: Date.now() + index,
            label: option.label || option,
            value: option.value || option
          }));
        }
      } catch (error) {
        console.error('解析字段选项失败:', error);
        options = [];
      }
    }
    setFieldOptions(options);
    
    // 设置表单值
    const formValues = {
      ...record,
      referenced_asset_type_id: referencedAssetTypeId
    };
    console.log('编辑字段 - 原始记录:', record);
    console.log('编辑字段 - 表单值:', formValues);
    console.log('编辑字段 - 当前字段类型:', record.field_type);
    fieldForm.setFieldsValue(formValues);
    setFieldModalVisible(true);
  };

  // 管理字段配置
  const handleManageFields = (record) => {
    setEditingType(record);
    fetchFieldConfigs(record.id);
    setFieldModalVisible(true);
  };

  // 关闭字段配置模态框
  const handleFieldModalClose = () => {
    setFieldModalVisible(false);
    setEditingField(null);
    setFieldOptions([]);
    setCurrentFieldType('');
    fieldForm.resetFields();
  };

  // 添加字段选项
  const addFieldOption = () => {
    const newOption = {
      key: Date.now(),
      label: '',
      value: ''
    };
    setFieldOptions([...fieldOptions, newOption]);
  };

  // 删除字段选项
  const removeFieldOption = (key) => {
    setFieldOptions(fieldOptions.filter(option => option.key !== key));
  };

  // 更新字段选项
  const updateFieldOption = (key, field, value) => {
    setFieldOptions(fieldOptions.map(option => 
      option.key === key ? { ...option, [field]: value } : option
    ));
  };

  // 将字段选项转换为JSON格式
  const fieldOptionsToJson = () => {
    if (currentFieldType === 'asset_reference') {
      // 资产关联类型，获取关联的资产类型ID
      const referencedAssetTypeId = fieldForm.getFieldValue('referenced_asset_type_id');
      if (!referencedAssetTypeId) return null;
      
      return JSON.stringify({
        referenced_asset_type_id: referencedAssetTypeId
      });
    }
    
    if (fieldOptions.length === 0) return null;
    
    const validOptions = fieldOptions.filter(option => 
      option.label.trim() && option.value.trim()
    );
    
    if (validOptions.length === 0) return null;
    
    return JSON.stringify({
      options: validOptions.map(option => ({
        label: option.label,
        value: option.value
      }))
    });
  };

  // 监听字段类型变化
  const handleFieldTypeChange = (value) => {
    console.log('字段类型变化:', value);
    setCurrentFieldType(value);
    // 如果不是select类型，清空字段选项
    if (value !== 'select') {
      setFieldOptions([]);
    }
    // 如果不是资产关联类型，清空关联的资产类型ID
    if (value !== 'asset_reference') {
      fieldForm.setFieldsValue({ referenced_asset_type_id: undefined });
    }
  };

  // 资产类型表格列
  const typeColumns = [
    {
      title: '类型名称',
      dataIndex: 'type_name',
      key: 'type_name',
    },
    {
      title: '类型代码',
      dataIndex: 'type_code',
      key: 'type_code',
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Tooltip title="编辑">
            <Button 
              type="link" 
              icon={<EditOutlined />} 
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="字段配置">
            <Button 
              type="link" 
              icon={<SettingOutlined />} 
              onClick={() => handleManageFields(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定要删除这个资产类型吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button 
                type="link" 
                danger 
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 字段配置表格列
  const fieldColumns = [
    {
      title: '字段名称',
      dataIndex: 'field_name',
      key: 'field_name',
    },
    {
      title: '字段代码',
      dataIndex: 'field_code',
      key: 'field_code',
    },
    {
      title: '字段类型',
      dataIndex: 'field_type',
      key: 'field_type',
      render: (type) => {
        const typeMap = {
          text: '文本',
          number: '数字',
          date: '日期',
          select: '选择',
          textarea: '多行文本',
          boolean: '布尔值',
          asset_reference: '资产关联'
        };
        return typeMap[type] || type;
      }
    },
    {
      title: '是否必填',
      dataIndex: 'is_required',
      key: 'is_required',
      render: (required) => (
        <Tag color={required ? 'red' : 'default'}>
          {required ? '必填' : '可选'}
        </Tag>
      ),
    },
    {
      title: '是否唯一',
      dataIndex: 'is_unique',
      key: 'is_unique',
      render: (unique) => (
        <Tag color={unique ? 'blue' : 'default'}>
          {unique ? '唯一' : '可重复'}
        </Tag>
      ),
    },
    {
      title: '显示顺序',
      dataIndex: 'display_order',
      key: 'display_order',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Tooltip title="编辑">
            <Button 
              type="link" 
              icon={<EditOutlined />} 
              onClick={() => handleFieldEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定要删除这个字段配置吗？"
            onConfirm={() => handleFieldDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button 
                type="link" 
                danger 
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 统计数据
  const statistics = {
    totalTypes: assetTypes.length,
    activeTypes: assetTypes.filter(type => type.is_active).length,
    totalFields: fieldConfigs.length,
    requiredFields: fieldConfigs.filter(field => field.is_required).length
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Row gutter={16} style={{ marginBottom: '16px' }}>
          <Col span={6}>
            <Statistic title="资产类型总数" value={statistics.totalTypes} />
          </Col>
          <Col span={6}>
            <Statistic title="启用类型" value={statistics.activeTypes} />
          </Col>
          <Col span={6}>
            <Statistic title="字段总数" value={statistics.totalFields} />
          </Col>
          <Col span={6}>
            <Statistic title="必填字段" value={statistics.requiredFields} />
          </Col>
        </Row>

        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingType(null);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            新建资产类型
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchAssetTypes}
          >
            刷新
          </Button>
        </div>

        <Table
          columns={typeColumns}
          dataSource={assetTypes}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
        />
      </Card>

      {/* 资产类型编辑模态框 */}
      <Modal
        title={editingType ? '编辑资产类型' : '新建资产类型'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingType(null);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="type_name"
            label="类型名称"
            rules={[{ required: true, message: '请输入类型名称' }]}
          >
            <Input placeholder="请输入类型名称" />
          </Form.Item>

          <Form.Item
            name="type_code"
            label="类型代码"
            rules={[{ required: true, message: '请输入类型代码' }]}
          >
            <Input placeholder="请输入类型代码（英文）" />
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
          >
            <TextArea rows={3} placeholder="请输入描述" />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="状态"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                {editingType ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 字段配置模态框 */}
      <Modal
        title={`字段配置 - ${editingType?.type_name || ''}`}
        open={fieldModalVisible}
        onCancel={handleFieldModalClose}
        footer={null}
        width={800}
      >
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingField({});
              setFieldOptions([]);
              setCurrentFieldType('');
              fieldForm.resetFields();
            }}
          >
            新建字段
          </Button>
        </div>

        <Table
          columns={fieldColumns}
          dataSource={fieldConfigs}
          rowKey="id"
          pagination={false}
          size="small"
        />

        {/* 字段编辑表单 */}
        {editingField !== null && (
          <Card title={editingField.id ? '编辑字段' : '新建字段'} style={{ marginTop: '16px' }}>
            <Form
              form={fieldForm}
              layout="vertical"
              onFinish={handleFieldSubmit}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="field_name"
                    label="字段名称"
                    rules={[{ required: true, message: '请输入字段名称' }]}
                  >
                    <Input placeholder="请输入字段名称" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="field_code"
                    label="字段代码"
                    rules={[{ required: true, message: '请输入字段代码' }]}
                  >
                    <Input placeholder="请输入字段代码（英文）" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="field_type"
                    label="字段类型"
                    rules={[{ required: true, message: '请选择字段类型' }]}
                  >
                    <Select 
                      placeholder="请选择字段类型"
                      onChange={handleFieldTypeChange}
                    >
                      <Option value="text">文本</Option>
                      <Option value="number">数字</Option>
                      <Option value="date">日期</Option>
                      <Option value="select">选择</Option>
                      <Option value="textarea">多行文本</Option>
                      <Option value="boolean">布尔值</Option>
                      <Option value="asset_reference">资产关联</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="display_order"
                    label="显示顺序"
                    initialValue={0}
                  >
                    <InputNumber min={0} placeholder="显示顺序" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              {currentFieldType === 'select' && (
                <Form.Item
                  label="字段选项（用于select类型）"
                  extra="配置下拉选择框的选项"
                >
                  <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '12px' }}>
                    {fieldOptions.map((option) => (
                      <div key={option.key} style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Input
                          placeholder="显示名称"
                          value={option.label}
                          onChange={(e) => updateFieldOption(option.key, 'label', e.target.value)}
                          style={{ flex: 1 }}
                        />
                        <Input
                          placeholder="选项值"
                          value={option.value}
                          onChange={(e) => updateFieldOption(option.key, 'value', e.target.value)}
                          style={{ flex: 1 }}
                        />
                        <Button
                          type="text"
                          danger
                          icon={<MinusCircleOutlined />}
                          onClick={() => removeFieldOption(option.key)}
                          style={{ flexShrink: 0 }}
                        />
                      </div>
                    ))}
                    
                    {fieldOptions.length === 0 && (
                      <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
                        暂无选项，点击下方按钮添加
                      </div>
                    )}
                    
                    <Button
                      type="dashed"
                      onClick={addFieldOption}
                      icon={<PlusCircleOutlined />}
                      style={{ width: '100%', marginTop: '8px' }}
                    >
                      添加选项
                    </Button>
                  </div>
                </Form.Item>
              )}

              {currentFieldType === 'asset_reference' && (
                <Form.Item
                  name="referenced_asset_type_id"
                  label="关联资产类型"
                  extra="选择要关联的资产类型，用户将可以从该类型的资产中选择"
                  rules={[{ required: true, message: '请选择要关联的资产类型' }]}
                >
                  <Select 
                    placeholder="请选择要关联的资产类型"
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {assetTypes
                      .filter(type => type.id !== editingType?.id) // 排除当前类型
                      .map(type => (
                        <Option key={type.id} value={type.id}>
                          {type.type_name} ({type.type_code})
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              )}

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="is_required"
                    label="是否必填"
                    valuePropName="checked"
                  >
                    <Switch checkedChildren="必填" unCheckedChildren="可选" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="is_unique"
                    label="是否唯一"
                    valuePropName="checked"
                  >
                    <Switch checkedChildren="唯一" unCheckedChildren="可重复" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => {
                    setEditingField(null);
                    setFieldOptions([]);
                    setCurrentFieldType('');
                  }}>
                    取消
                  </Button>
                  <Button type="primary" htmlType="submit">
                    {editingField.id ? '更新' : '创建'}
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        )}
      </Modal>
    </div>
  );
};

export default AssetTypesManagement;
